import {
  useMutation,
  useQueryClient,
  UseMutationResult,
  UseMutationOptions,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import type { AnnouncementWithRelationsDto, ResponseDto } from "@mimber/schema"
import { announcementsKeys } from "../announcements.utils"

interface DeleteAnnouncementOptions extends Omit<
  UseMutationOptions<ResponseDto<AnnouncementWithRelationsDto>, Error, void>,
  "mutationFn"
> {}

export const useDeleteAnnouncement = (
  announcementUuid: string,
  options: DeleteAnnouncementOptions = {}
): UseMutationResult<
  ResponseDto<AnnouncementWithRelationsDto>,
  Error,
  void
> => {
  guardAppUsage("mimber-organization", "useDeleteAnnouncement")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async () => {
      const response = await server.delete<
        ResponseDto<AnnouncementWithRelationsDto>
      >(`announcements/${announcementUuid}`)
      return response.data
    },
    onSuccess: (response, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: announcementsKeys.findAll(),
      })
      queryClient.removeQueries({
        queryKey: announcementsKeys.findOne(announcementUuid),
      })

      if (options?.onSuccess) {
        options.onSuccess(response, variables, onMutateResult, context)
      }
    },
  })
}
